import i18n from "i18next";
import { z } from "zod";
import { notificationSchema } from "../utils";
import {
  createAtcSchema,
  PORT_SERVICE_FIELDS_LANG_BASE,
  withAtcAfterAtsValidation,
} from "./sharedClosePortServiceSchemas";

const LANG_BASE = PORT_SERVICE_FIELDS_LANG_BASE;

const createActualNumberOfSkipsSchema = () =>
  z.object({
    actualNumberOfSkips: z.preprocess(
      (value) => {
        if (value === "" || value === null || value === undefined) return undefined;

        const parsed = Number(value);
        return Number.isNaN(parsed) ? value : parsed;
      },
      z.number({
        required_error: i18n.t("form-validation.required", {
          label: i18n.t(`${LANG_BASE}.actual-number-of-skips`),
        }),
        invalid_type_error: i18n.t("form-validation.invalid-number", {
          label: i18n.t(`${LANG_BASE}.actual-number-of-skips`),
        }),
      }),
    ),
  });

const createWasteResiduesForDischargeFacilitySchema = () =>
  z.object({
    wasteResiduesForDischargeFacility: z
      .array(
        z.object({
          marpolAnnexIOil: z.string().max(100),
          otherSpecifyDetails: z.string().max(150),
          quantityM3: z.number().positive(),
        }),
        {
          required_error: i18n.t("form-validation.required", {
            label: i18n.t(`${LANG_BASE}.wasteResiduesForDischargeFacility`),
          }),
          invalid_type_error: i18n.t("form-validation.required", {
            label: i18n.t(`${LANG_BASE}.wasteResiduesForDischargeFacility`),
          }),
        },
      )
      .superRefine((wastes, ctx) => {
        if (!Array.isArray(wastes) || wastes.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t("form-validation.required", {
              label: i18n.t(`${LANG_BASE}.wasteResiduesForDischargeFacility`),
            }),
          });
          return;
        }

        wastes.forEach((waste, index) => {
          if (waste.marpolAnnexIOil === "Other (Specify)" && !waste.otherSpecifyDetails.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t("form-validation.required", {
                label: i18n.t(`${LANG_BASE}.wasteResiduesForDischargeFacility`),
              }),
              path: [index, "otherSpecifyDetails"],
            });
          }
        });
      }),
  });

const imagesFieldsSchema = z.object({
  images: z
    .array(
      z.object({
        documentUrl: z.string(),
        documentName: z.string(),
        fileType: z.string(),
      }),
      {
        required_error: i18n.t("form-validation.required", {
          label: i18n.t(`${LANG_BASE}.images`),
        }),
        invalid_type_error: i18n.t("form-validation.required", {
          label: i18n.t(`${LANG_BASE}.images`),
        }),
      },
    )
    .superRefine((value, ctx) => {
      if (!Array.isArray(value) || value.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t("form-validation.required", { label: i18n.t(`${LANG_BASE}.images`) }),
        });
      }
    }),
  serviceReportAttachment: z
    .object({
      documentUrl: z.string({
        required_error: i18n.t("form-validation.required", {
          label: i18n.t(`${LANG_BASE}.service-report`),
        }),
        invalid_type_error: i18n.t("form-validation.invalid-type", {
          label: i18n.t(`${LANG_BASE}.service-report`),
        }),
      }),
      documentName: z.string({
        required_error: i18n.t("form-validation.required", {
          label: i18n.t(`${LANG_BASE}.service-report`),
        }),
        invalid_type_error: i18n.t("form-validation.invalid-type", {
          label: i18n.t(`${LANG_BASE}.service-report`),
        }),
      }),
      fileType: z.string({
        required_error: i18n.t("form-validation.required", {
          label: i18n.t(`${LANG_BASE}.service-report`),
        }),
        invalid_type_error: i18n.t("form-validation.invalid-type", {
          label: i18n.t(`${LANG_BASE}.service-report`),
        }),
      }),
    })
    .required(),
});

export const closeSludgeDisposalSchema = (ats?: string | Date | null) =>
  withAtcAfterAtsValidation(
    (createAtcSchema("required") as z.AnyZodObject)
      .merge(createWasteResiduesForDischargeFacilitySchema())
      .merge(createActualNumberOfSkipsSchema())
      .merge(imagesFieldsSchema)
      .merge(notificationSchema)
      .superRefine((val, ctx) => {
        const serviceReportAttachment = val.serviceReportAttachment as
          | { documentUrl?: string }
          | undefined;
        const hasServiceReport = !!serviceReportAttachment?.documentUrl?.trim();

        if (!hasServiceReport) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["serviceReportAttachment"],
            message: i18n.t("form-validation.required", {
              label: i18n.t(`${LANG_BASE}.service-report`),
            }),
          });
        }
      }),
    ats,
  );

export type CloseSludgeDisposalSchema = z.infer<ReturnType<typeof closeSludgeDisposalSchema>>;
